import type { BlockPromptContext } from './types.ts';
import { buildProgressionClause, buildExampleClause, buildCrossMonthClause } from './types.ts';
import { buildPersonalizationClause } from './personalization-helpers.ts';
import { SOURCE_LANGUAGES, TARGET_LANGUAGES } from '../../constants/languages.ts';

export function buildGrammarExplanationPrompt(ctx: BlockPromptContext): string {
  const source = SOURCE_LANGUAGES.find((l) => l.code === ctx.profile.native_language);
  const target = TARGET_LANGUAGES.find((l) => l.code === ctx.profile.target_language);

  return `Generate a **Grammar Explanation** block for a ${ctx.profile.level.toUpperCase()} ${target?.name} learner whose native language is ${source?.name}.

## Theme
Workbook theme: "${ctx.workbookTheme}"
Sub-theme for this block: "${ctx.subTheme}"

## Block Position
This is block ${ctx.blockOrder} of ${ctx.totalBlocks}.
${buildPersonalizationClause(ctx.profile, ctx.blockOrder, ctx.isPreview)}

## Requirements

1. Pick a **grammar topic** that is appropriate for ${ctx.profile.level.toUpperCase()} level and relevant to "${ctx.subTheme}".
   - A1-A2: basic verb tenses (present), articles, gender, common prepositions, basic adjective agreement
   - B1: past tenses, subjunctive introduction, object pronouns, comparative/superlative
   - B2+: subjunctive uses, conditional, passive voice, advanced pronoun placement
2. Write a clear, concise **explanation** of the rule in ${source?.name} (the learner's native language).
3. Provide a **pattern** or formula if applicable (e.g., "stem + -o, -es, -e, -emos, -en").
4. Give **3-5 annotated examples**:
   - \`target\`: A complete sentence in ${target?.name} demonstrating the rule
   - \`native\`: Translation in ${source?.name}
   - \`highlight\`: The specific grammar element in the target sentence being demonstrated (must be a substring of \`target\`)
5. List **2-3 common mistakes** that ${source?.name}-speaking learners make with this grammar point. Write these in ${source?.name}.
6. Create **2-3 quick practice items** (lightweight — fill-in-the-blank or choose-the-correct-form):
   - \`question\`: The exercise prompt in ${target?.name} (use "___" for blanks)
   - \`answer\`: The correct answer
   - \`hint\`: A hint in ${source?.name}

## Output Format

Return a single JSON object matching:

\`\`\`typescript
interface GrammarExplanationBlock {
  id: string;
  type: "grammar_explanation";
  order: ${ctx.blockOrder};
  title: string;           // In ${target?.name}
  titleNative: string;     // In ${source?.name}
  theme: "${ctx.subTheme}";
  estimatedPages: 1.5;
  topic: string;           // Grammar topic in ${target?.name}
  topicNative: string;     // Grammar topic in ${source?.name}
  explanation: string;     // Rule explanation in ${source?.name}
  pattern?: string;        // Formula/pattern (optional)
  examples: Array<{
    target: string;
    native: string;
    highlight: string;
  }>;
  commonMistakes: string[];  // In ${source?.name}
  practice: Array<{
    question: string;
    answer: string;
    hint: string;
  }>;
}
\`\`\`

Respond with ONLY the JSON object.${buildProgressionClause(ctx)}${buildExampleClause(ctx, 'grammar_explanation')}${buildCrossMonthClause(ctx)}`;
}
